/**LAGOSStateIntelligence — /state/lagos/intelligence — UPGRADED */
import React, { useEffect, useState } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { PageHeader, SectionCard } from "@/components/ds";
import client from "@/api/client";

const fmtN=(v,u="")=>{ if(!v&&v!==0)return"—"; const n=Number(v); if(n>=1e6)return`${(n/1e6).toFixed(1)}M ${u}`.trim(); if(n>=1000)return`${(n/1000).toFixed(0)}K ${u}`.trim(); return`${n.toLocaleString()} ${u}`.trim(); };
const fmtU=(v)=>{ if(!v)return"—"; const n=Number(v); if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`; if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`; if(n>=1000)return`$${(n/1000).toFixed(0)}K`; return`$${n}`; };

// Intervention signals per LGA
const LGA_SIGNALS = {
  Apapa:        { action: "Scale immediately", color:"#059669", note:"Recycling cluster anchor; Art.6.4 + VCM ready; Platinum performers present" },
  Alimosho:     { action: "Priority intervention", color:"#dc2626", note:"Largest LGA; 180K+ t/yr waste handling; high reduction potential; formal fleet transition needed" },
  Ikorodu:      { action: "PoA anchor — agricultural", color:"#1d4ed8", note:"Rice + poultry methane cluster; Art.6.4 eligible; NIRSAL CSA loan integration point" },
  "Eti-Osa":    { action: "Scale solar/circular", color:"#059669", note:"Premium market; solar leaders; circular economy pioneers; Platinum concentration" },
  Surulere:     { action: "Urban circular — grow", color:"#d97706", note:"Composting + reuse hub; community-scale; evidence upgrade needed" },
  Ikeja:        { action: "Industrial decarbonisation", color:"#7c3aed", note:"SME manufacturing; energy audit priority; cold chain + food processing target" },
  "Oshodi-Isolo":{ action:"Manufacturing efficiency", color:"#d97706", note:"Dense industrial zone; medium performers; solar + efficiency financing opportunity" },
  Mushin:       { action: "Formalise informal sector", color:"#94a3b8", note:"High social impact; low data confidence; prioritise evidence upgrade + TA" },
  Badagry:      { action: "Rural agro-solar expansion", color:"#059669", note:"Agro-solar synergy; village-scale; replicable model for rural clusters" },
  Agege:        { action: "Composting scale-up", color:"#d97706", note:"CPA-WASTE anchor in Agege; near Gold; evidence upgrade = Platinum potential" },
};

export default function LAGOSStateIntelligence() {
  const { selectedPeriod, portfolio } = useLagos();
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoading(true);
    client.get(`/lagos/state-intelligence/${selectedPeriod}`).then(r=>setIntel(r.data)).finally(()=>setLoading(false));
  }, [selectedPeriod]);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading state intelligence…</div>;
  const p = intel?.portfolio || portfolio || {};
  const mon = intel?.monetisation || {};

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="STATE INTELLIGENCE & AGGREGATION" title="" />
      <p className="text-xs text-slate-400 -mt-4">Top sectors · Top LGAs · Prioritized interventions · Recommended action signals · Portfolio monetisation</p>

      {/* State Totals */}
      <div className="grid grid-cols-4 gap-3">
        {[["State Baseline",fmtN(p.total_tco2e_yr,"tCO₂e/yr"),"Operational emissions","#dc2626"],["Reduction Potential",fmtN(p.reduction_potential_tco2e,"tCO₂e/yr"),"Portfolio-wide target","#059669"],["Carbon Value (Base)",fmtU(mon.carbon_base||p.carbon_value_base_usd)+"/yr","Indicative — VCM+Art.6.4","#1d4ed8"],["Portfolio Readiness",(p.readiness_score_avg||0).toFixed(0)+"/100","Avg DMRV score","#d97706"]].map(([l,v,s,c])=>(
          <div key={l} className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-[10px] text-slate-400">{l}</p>
            <p className="text-xl font-black mt-1" style={{color:c}}>{v}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{s}</p>
          </div>
        ))}
      </div>

      {/* Top-3 Decision Panels */}
      <div className="grid grid-cols-3 gap-4">
        {[
          {title:"Top 3 Immediate Priorities",color:"#dc2626",bg:"#fef2f2",items:[
            {rank:1,name:"CPA-WASTE-DIVERSION (Alimosho)",why:"120K tCO₂e/yr · Art.6.4 eligible · LAWMA anchor available · Highest combined value",action:"Engage LAWMA, install weigh-bridges, apply NCCC pre-application"},
            {rank:2,name:"CPA-RECYCLING (Apapa)",why:"170K+ tCO₂e/yr VCM · 5 Platinum entities · DOE-ready evidence · Fastest to register",action:"Register Verra VCS PDD, aggregate top 6 recyclers, engage accredited DOE"},
            {rank:3,name:"ENT-ALI-003 Fleet Transition",why:"5.8M km/yr · 246 tCO₂e/yr baseline · CNG/EV conversion viable",action:"Engage Lagos Bus Rapid Transit; propose GreenFleet CNG financing with LASG"},
          ]},
          {title:"Top 3 Quick Wins (3–6 months)",color:"#d97706",bg:"#fffbeb",items:[
            {rank:1,name:"Eco-Label Registration (7 Platinum)",why:"Pre-qualified · Full evidence · DeCarbonIQ certificate ready · Public procurement advantage",action:"Issue official Lagos State Eco-Label Certificates. Gazette the programme."},
            {rank:2,name:"Eti-Osa Solar MRV Upgrade",why:"T1 evidence already collected · VCM fast-track possible · High composite scores",action:"Commission DOE desk review for AMS-I.A validation. Target first credits: 18 months."},
            {rank:3,name:"Ikorodu Biodigester Pilot (5 units)",why:"NIRSAL CSA loans available · NDC priority · Art.6.4 eligible · Social impact",action:"Submit NIRSAL CSA Equipment Loan application for 5 pilot biodigesters."},
          ]},
          {title:"Top 3 Long-Term Plays (12–36 months)",color:"#1d4ed8",bg:"#eff6ff",items:[
            {rank:1,name:"Lagos State PoA Registration",why:"$7.1M/yr carbon value · State coordinating entity mandate · 4 CPAs eligible",action:"Register state PoA with UNFCCC PACM supervisory body. Appoint DOE. Engage NCCC."},
            {rank:2,name:"1,000+ Entity Onboarding",why:"Platform scales infinitely · Carbon portfolio multiplies proportionally",action:"Launch public entity registration programme. Partner LIRS for business registration integration."},
            {rank:3,name:"Lagos Green Bond Issuance",why:"$7M+/yr carbon revenue = interest coverage · DFI appetite confirmed",action:"Structure Lagos State Green Bond backed by verified carbon asset pipeline. Engage AfDB/IFC."},
          ]},
        ].map(({title,color,bg,items})=>(
          <div key={title} className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="px-4 py-3 rounded-t-xl border-b border-slate-100" style={{background:bg}}>
              <p className="text-xs font-black" style={{color}}>{title}</p>
            </div>
            <div className="p-4 space-y-3">
              {items.map(({rank,name,why,action})=>(
                <div key={rank} className="border-b border-slate-50 pb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center text-white" style={{background:color}}>{rank}</span>
                    <p className="text-[10px] font-bold text-slate-800">{name}</p>
                  </div>
                  <p className="text-[9px] text-slate-500 mb-1">{why}</p>
                  <p className="text-[9px] font-medium" style={{color}}>→ {action}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Existing panels */}
      <div className="grid grid-cols-2 gap-4">
        {/* Top Sectors by Reduction */}
        <SectionCard title="Priority Sectors — Reduction Potential + % Share">
          <div className="p-4 space-y-3">
            {(() => {
              const sectors = (intel?.sector_priority || Object.entries(p.by_sector||{}).sort((a,b)=>b[1].reduction-a[1].reduction).slice(0,5).map(([s,d])=>({sector:s,...d})));
              const maxRed = Math.max(...sectors.map(s=>s.reduction||0), 1);
              const totalRed = sectors.reduce((s,x)=>s+(x.reduction||0),0);
              return sectors.map((s,i)=>(
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-slate-300 w-4">{i+1}</span>
                      <span className="font-medium text-slate-700 capitalize">{(s.sector||"").replace(/_/g," ")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{totalRed>0?((s.reduction/totalRed)*100).toFixed(0):0}%</span>
                      <span className="font-black text-emerald-700">{fmtN(s.reduction,"tCO₂e")}</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500" style={{width:`${(s.reduction/maxRed)*100}%`}} />
                  </div>
                  <p className="text-[9px] text-slate-400">{s.count} entities · {fmtN(s.tco2e,"tCO₂e")} baseline</p>
                </div>
              ));
            })()}
          </div>
        </SectionCard>

        {/* Priority LGAs */}
        <SectionCard title="Priority LGAs — Reduction + Action Signal">
          <div className="p-4 space-y-2">
            {(() => {
              const lgaEntries = Object.entries(p.by_lga||{}).sort((a,b)=>b[1].reduction-a[1].reduction);
              const maxRed = Math.max(...lgaEntries.map(([,d])=>d.reduction||0), 1);
              const totalRed = lgaEntries.reduce((s,[,d])=>s+(d.reduction||0),0);
              return lgaEntries.map(([lga,d])=>{
                const sig = LGA_SIGNALS[lga] || { action:"Monitor",color:"#94a3b8",note:"" };
                const pct = totalRed > 0 ? ((d.reduction/totalRed)*100).toFixed(0) : 0;
                return (
                  <div key={lga} className="space-y-1 py-1 border-b border-slate-50 last:border-0">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-700">{lga}</span>
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold text-white" style={{background:sig.color}}>{sig.action}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400">{pct}%</span>
                        <span className="text-xs font-black text-emerald-700">{fmtN(d.reduction,"tCO₂e")}</span>
                      </div>
                    </div>
                    <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{width:`${(d.reduction/maxRed)*100}%`,background:sig.color}} />
                    </div>
                    <p className="text-[9px] text-slate-400">{d.count} entities</p>
                  </div>
                );
              });
            })()}
          </div>
        </SectionCard>
      </div>

      {/* Prioritized Intervention Areas */}
      <SectionCard title="Recommended Intervention Matrix — by Cluster">
        <div className="p-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              {cluster:"SCALE IMMEDIATELY",color:"#059669",bg:"#ecfdf5",entities:["Apapa Recycling (CPA-RECYCLING)","Alimosho Waste Diversion (CPA-WASTE)","Ikorodu Agricultural Methane"],rationale:"High reduction potential · VCM/Art.6.4 eligible · Evidence quality supports verification"},
              {cluster:"PILOT FIRST",color:"#1d4ed8",bg:"#eff6ff",entities:["Eti-Osa Solar Cluster","Ikorodu Biodigester Pilot","Surulere Composting Hub"],rationale:"Technology proven · Business model needs validation · Revenue model needs CRG + carbon linkage"},
              {cluster:"FORMALISE & SUPPORT",color:"#d97706",bg:"#fffbeb",entities:["Mushin Informal Recyclers","Badagry Agro-Solar","Agege Composting Coop"],rationale:"High social impact · Low data confidence · Evidence upgrade + TA needed before carbon structuring"},
            ].map(({cluster,color,bg,entities,rationale})=>(
              <div key={cluster} className="rounded-xl p-4 border" style={{background:bg,borderColor:color+"33"}}>
                <p className="text-xs font-black mb-3" style={{color}}>{cluster}</p>
                <ul className="space-y-1.5 mb-3">
                  {entities.map(e=><li key={e} className="text-[10px] text-slate-600 flex gap-1.5"><span style={{color}}>→</span>{e}</li>)}
                </ul>
                <p className="text-[9px] text-slate-500 italic">{rationale}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Monetisation */}
      <SectionCard title="Portfolio Monetisation Potential">
        <div className="p-4 grid grid-cols-3 gap-4">
          {[["Total Reduction Potential",fmtN(p.reduction_potential_tco2e,"tCO₂e/yr"),"All 74 entities combined"],["Carbon Value (Low $10/t)",fmtU(p.carbon_value_low_usd)+"/yr","Conservative scenario"],["Carbon Value (Base $20/t)",fmtU(p.carbon_value_base_usd)+"/yr","Market expectation"],["Carbon Value (High $40/t)",fmtU(p.carbon_value_high_usd)+"/yr","Aggressive scenario"],["VCM Eligible",fmtN(p.vcm_eligible_count)+" entities","AMS methodology path"],["Art.6.4 Eligible",fmtN(p.art6_eligible_count)+" entities","Conditional on NCCC"]].map(([l,v,s])=>(
            <div key={l} className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-400">{l}</p>
              <p className="text-lg font-black text-slate-900">{v}</p>
              <p className="text-[9px] text-slate-400 mt-0.5">{s}</p>
            </div>
          ))}
        </div>
        <div className="px-4 pb-4">
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            <strong>Mandatory disclosure:</strong> All carbon values are indicative. Conditional on VVB validation, registry issuance, and (for Art.6.4) NCCC corresponding adjustment. Must NOT be included in budget commitments until formally validated.
          </p>
        </div>
      </SectionCard>
    </div>
  );
}
